import React from "react";
import { formatDecimal } from "@/utils/number/formatters";
import styles from "./AggregateTooltipContent.module.css";

export interface AggregateData {
  bookedHours: number;
  possibleHours: number;
  availableHours: number;
  percentage: number;
  projectHours?: number;
  otherHours?: number;
}

export type TooltipVariant =
  | "booked"
  | "project"
  | "available"
  | "percent"
  | "summary";

interface AggregateTooltipContentProps {
  data: AggregateData;
  variant: TooltipVariant;
}

/**
 * AggregateTooltipContent - Formatted display for aggregate row tooltips
 *
 * Variants:
 * - "booked": Single row showing booked hours
 * - "project": Single row showing project hours
 * - "available": Single row showing available/overflow hours
 * - "percent": Single row showing percentage info
 * - "summary": Full table with all information combined
 */
export const AggregateTooltipContent: React.FC<
  AggregateTooltipContentProps
> = ({ data, variant }) => {
  const {
    bookedHours,
    possibleHours,
    availableHours,
    percentage,
    projectHours = 0,
    otherHours = 0,
  } = data;
  const isOverbooked = availableHours < 0;

  // Simple single-value variants
  if (variant === "booked") {
    return (
      <div className={styles.simple}>
        <span className={styles.label}>Booket</span>
        <span className={styles.value}>
          {formatDecimal(otherHours || bookedHours)} T
        </span>
      </div>
    );
  }

  if (variant === "project") {
    return (
      <div className={styles.simple}>
        <span className={styles.label}>Prosjekt</span>
        <span className={styles.value}>{formatDecimal(projectHours)} T</span>
      </div>
    );
  }

  if (variant === "available") {
    if (isOverbooked) {
      return (
        <div className={styles.simple}>
          <span className={styles.label}>Over kapasitet</span>
          <span className={styles.valueOverflow}>
            {formatDecimal(Math.abs(availableHours))} T
          </span>
        </div>
      );
    }
    return (
      <div className={styles.simple}>
        <span className={styles.label}>Ledig</span>
        <span className={styles.value}>{formatDecimal(availableHours)} T</span>
      </div>
    );
  }

  if (variant === "percent") {
    const percentDisplay = Math.round(percentage * 100);
    let status = "Delvis booket";
    if (percentage === 0) status = "Ingen bookinger";
    else if (percentage > 1) status = "Overbooket";
    else if (percentage >= 0.72) status = "Fullt booket";

    return (
      <div className={styles.simple}>
        <span className={styles.label}>{status}</span>
        <span className={styles.value}>{percentDisplay}%</span>
      </div>
    );
  }

  // Summary variant - full table layout
  const percentDisplay = Math.round(percentage * 100);

  // Determine percentage color class to match badge colors
  const getPercentColorClass = () => {
    if (percentage === 0) return styles.percentEmpty; // Grey for 0%
    if (percentage > 1) return styles.percentOverflow; // Red for >100%
    if (percentage >= 0.72) return styles.percentOk; // Green for 72-100%
    return styles.percentPartial; // Orange for 1-71%
  };

  return (
    <div className={styles.summary}>
      <div className={styles.header}>
        <span className={styles.title}>Kapasitetsoversikt</span>
        <span className={getPercentColorClass()}>{percentDisplay}%</span>
      </div>

      <table className={styles.table}>
        <tbody>
          <tr>
            <td className={styles.rowLabel}>Kapasitet</td>
            <td className={styles.rowValue}>
              {formatDecimal(possibleHours)} T
            </td>
          </tr>
          <tr>
            <td className={styles.rowLabel}>Booket totalt</td>
            <td className={styles.rowValue}>{formatDecimal(bookedHours)} T</td>
          </tr>
          {projectHours > 0 && (
            <tr className={styles.subRow}>
              <td className={styles.rowLabelSub}>↳ Valgt prosjekt</td>
              <td className={styles.rowValue}>
                {formatDecimal(projectHours)} T
              </td>
            </tr>
          )}
          {otherHours > 0 && projectHours > 0 && (
            <tr className={styles.subRow}>
              <td className={styles.rowLabelSub}>↳ Andre</td>
              <td className={styles.rowValue}>{formatDecimal(otherHours)} T</td>
            </tr>
          )}
          <tr className={styles.resultRow}>
            <td className={styles.rowLabel}>
              {isOverbooked ? "Over kapasitet" : "Ledig"}
            </td>
            <td
              className={
                isOverbooked ? styles.rowValueOverflow : styles.rowValueOk
              }
            >
              {isOverbooked
                ? `${formatDecimal(Math.abs(availableHours))} T`
                : `${formatDecimal(availableHours)} T`}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default AggregateTooltipContent;
